import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { errorFromResponse } from '@/lib/errors';
import type { HomeBannerAdminView, HomeBannerRequest, CmsPage } from '../types/cms';

const BASE = '/admin/v1/home-banners';
const REORDER_PATH = `${BASE}/reorder`;

const fetchBanners = async (page: number, size: number): Promise<CmsPage<HomeBannerAdminView>> => {
  const q = new URLSearchParams({ page: String(page), size: String(size), sort: 'sortOrder,asc' });
  const res = await adminFetch(`${BASE}?${q}`);
  if (!res.ok) throw new Error(`home-banners list failed: ${res.status}`);
  const env = (await res.json()) as { data?: HomeBannerAdminView[] | CmsPage<HomeBannerAdminView> };
  const raw = env.data;
  if (Array.isArray(raw)) return { content: raw, totalElements: raw.length, totalPages: 1, number: 0, size };
  return raw ?? { content: [], totalElements: 0, totalPages: 0, number: 0, size };
};

const fetchBanner = async (id: number): Promise<HomeBannerAdminView> => {
  const res = await adminFetch(`${BASE}/${id}`);
  if (!res.ok) throw new Error(`home-banner fetch failed: ${res.status}`);
  const env = (await res.json()) as { data?: HomeBannerAdminView };
  if (!env.data) throw new Error('no data');
  return env.data;
};

export const homeBannersQueryKey = (page: number, size: number) =>
  ['admin', 'home-banners', page, size] as const;

export const homeBannerQueryKey = (id: number) =>
  ['admin', 'home-banner', id] as const;

export const useHomeBannersQuery = (page = 0, size = 20) =>
  useQuery({ queryKey: homeBannersQueryKey(page, size), queryFn: () => fetchBanners(page, size) });

export const useHomeBannerQuery = (id: number) =>
  useQuery({ queryKey: homeBannerQueryKey(id), queryFn: () => fetchBanner(id), enabled: id > 0 });

export const useHomeBannerMutation = () => {
  const qc = useQueryClient();
  // 수정 후 다시 열면 이전 값이 보이던 문제 — 목록 키만 무효화하고 단건 키(['admin', 'home-banner', id])는 그대로라
  // staleTime(30초) 안에는 캐시된 옛 값이 그대로 폼에 들어갔다. 단건 캐시도 함께 무효화한다.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin', 'home-banners'] }),
      qc.invalidateQueries({ queryKey: ['admin', 'home-banner'] }),
    ]);

  const create = useMutation({
    mutationFn: async (body: HomeBannerRequest) => {
      const res = await adminFetch(BASE, { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: HomeBannerRequest }) => {
      const res = await adminFetch(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(body) });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await adminFetch(`${BASE}/${id}`, { method: 'DELETE',  });
      if (!res.ok) throw await errorFromResponse(res);
    },
    onSuccess: invalidate,
  });

  // ID45: 드래그 정렬 영속. body {bannerIds:[Long...]} → 응답은 새 순서 목록.
  // /admin/v1/home-banners/reorder 는 writeAllowlist 등록 경로 → Idempotency-Key 필수.
  const reorder = useMutation({
    mutationFn: async (bannerIds: number[]): Promise<HomeBannerAdminView[]> => {
      const body = { bannerIds };
      const res = await adminFetch(REORDER_PATH, {
        method: 'PUT',
        headers: {
          'Idempotency-Key': idempotencyKeyFor('PUT', REORDER_PATH, body),
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`banner reorder failed: ${res.status}`);
      const env = (await res.json()) as { data?: HomeBannerAdminView[] | null };
      return env.data ?? [];
    },
    onSuccess: invalidate,
  });

  return { create, update, remove, reorder };
};
